import React from "react";
import {
	Button,
	ButtonProps,
	ChildrenProps,
	ClassNameProps,
	DefaultContextValue,
	makeClassName,
	Row,
	StyleProps,
} from "@hex-insights/core";
import styles from "./styles.module.css";

type ButtonSwitchContextValue = {
	value: string;
	setValue: (value: string) => void;
};

const ButtonSwitchContext = React.createContext<ButtonSwitchContextValue>(
	new DefaultContextValue("ButtonSwitchContext") as any,
);

export type ButtonSwitchProps = {
	value: string;
	setValue: (value: string) => void;
} & Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function ButtonSwitch({ value, setValue, className, style, children }: ButtonSwitchProps) {
	const contextValue = React.useMemo(() => ({ value, setValue }), [value, setValue]);

	return (
		<ButtonSwitchContext.Provider value={contextValue}>
			<Row className={className} style={style}>
				{children}
			</Row>
		</ButtonSwitchContext.Provider>
	);
}

export type ButtonSwitchButtonProps = {
	value: string;
} & Omit<ButtonProps, "onClick" | "variant">;

ButtonSwitch.Button = function ({ value: thisValue, className, ...props }: ButtonSwitchButtonProps) {
	const { value, setValue } = React.useContext(ButtonSwitchContext);

	const isSelected = thisValue === value;

	const onClick = React.useCallback(() => {
		setValue(thisValue);
	}, [setValue, thisValue]);

	return (
		<Button
			{...props}
			variant={isSelected ? "primary" : "link"}
			onClick={onClick}
			className={makeClassName(
				styles["button-switch__button"],
				isSelected ? "" : styles["button-switch__button--not-selected"],
				className,
			)}
		/>
	);
};
